import React, { createContext, useContext, useState, useEffect } from 'react';
import errorTracker from '../utils/errorTracker';

// Create context
const ErrorContext = createContext();

// Error tracking provider component
export const ErrorProvider = ({ children }) => {
  const [errors, setErrors] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Initialize error tracking on component mount
  useEffect(() => {
    if (!isInitialized) {
      errorTracker.init({
        captureUnhandledErrors: true,
        capturePromiseRejections: true,
        captureNetworkErrors: true,
        reportErrors: true,
      });
      
      setIsInitialized(true);
    }
    
    // Update errors when they change
    const updateErrors = () => {
      setErrors(errorTracker.getRecentErrors());
    };
    
    // Set initial errors
    updateErrors();
    
    // Set up interval to check for new errors - reduced frequency to avoid "random" notifications
    const intervalId = setInterval(updateErrors, 30000); // Changed from 5s to 30s
    
    return () => {
      clearInterval(intervalId);
    };
  }, [isInitialized]);
  
  // Manual error capturing function
  const captureError = (error, additionalInfo = {}) => {
    errorTracker.captureError(error, additionalInfo);
    setErrors(errorTracker.getRecentErrors());
  };
  
  // Clear errors
  const clearErrors = () => {
    errorTracker.clearErrors();
    setErrors([]);
  };
  
  // Error boundary method to catch render errors
  const handleComponentError = (error, errorInfo) => {
    errorTracker.captureError(error, { 
      type: 'react_error',
      componentStack: errorInfo?.componentStack,
    });
    setErrors(errorTracker.getRecentErrors());
  };
  
  // Context value
  const contextValue = {
    errors,
    errorCount: errors.length,
    captureError,
    clearErrors,
    handleComponentError,
  };
  
  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

// Hook for accessing error context
export const useError = () => {
  const context = useContext(ErrorContext);
  
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  
  return context;
};

// Error boundary component
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  
  componentDidCatch(error, errorInfo) {
    // Report error to error tracking system
    errorTracker.captureError(error, {
      type: 'react_error_boundary',
      componentStack: errorInfo?.componentStack,
    });
    
    // Call onError prop if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }
  
  render() {
    const { fallback, children } = this.props;

    if (this.state.hasError) {
      if (typeof fallback === 'function') {
        return fallback(this.state.error);
      }

      // Determine specific error message based on error type
      const error = this.state.error;
      let errorTitle = 'Something went wrong';
      let errorMessage = 'The application encountered an error. Please try again or contact support.';
      let actionLabel = 'Try Again';

      if (error?.message?.includes('ChunkLoadError') || error?.message?.includes('Loading chunk')) {
        errorTitle = 'Application Update Available';
        errorMessage = 'A new version of the application is available. Please refresh the page to get the latest updates.';
        actionLabel = 'Refresh Page';
      } else if (error?.message?.includes('Network') || error?.message?.includes('fetch')) {
        errorTitle = 'Connection Problem';
        errorMessage = 'We\'re having trouble connecting to our servers. Please check your internet connection and try again.';
        actionLabel = 'Retry';
      } else if (error?.message?.includes('ResizeObserver')) {
        // This is a common browser warning that doesn't affect functionality
        errorTitle = 'Display Issue';
        errorMessage = 'A minor display issue occurred. You can continue using the application.';
        actionLabel = 'Continue';
      }

      return fallback || (
        <div className="error-boundary" style={{
          padding: '2rem',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '2rem auto'
        }}>
          <h2>{errorTitle}</h2>
          <p style={{ marginBottom: '1.5rem' }}>{errorMessage}</p>
          <button
            onClick={() => {
              if (actionLabel === 'Refresh Page') {
                window.location.reload();
              } else {
                this.setState({ hasError: false, error: null });
              }
            }}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {actionLabel}
          </button>
          {process.env.NODE_ENV === 'development' && error?.stack && (
            <details style={{ marginTop: '2rem', textAlign: 'left' }}>
              <summary style={{ cursor: 'pointer' }}>Developer Details</summary>
              <pre style={{
                backgroundColor: '#f5f5f5',
                padding: '1rem',
                overflow: 'auto',
                fontSize: '0.875rem'
              }}>
                {error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return children;
  }
}

export default ErrorContext;